<template>
  <div :class="`card-loan-${loan.reference}`">
    <div v-if="loan.extraCash" class="position-relative mt-4">
      <div class="badge badge-success card-status px-2 py-1 position-absolute">
        {{ $t('LoanCard.extraCash') }}
      </div>
    </div>
    <div class="card card-shadow border-0 card-clickable card-rounded mb-3" @click="openLoanDetails">
      <LoanOverdueMessage :loan="loan" v-if="loan.status === 'DELAYED'"></LoanOverdueMessage>
      <LoanUnpaidMessage :loan="loan" v-if="loan.status === 'NORMAL_UNPAID'"/>
      <LoanDefaultedMessage v-else-if="loan.status === 'DEFAULTED' || loan.subStatus === 'RESTRUCTURED'"
                            :status="loan.subStatus"></LoanDefaultedMessage>
      <div class="card-body" v-if="['CLOSED', 'REFUNDED'].includes(loan.status)">
        <div class="d-flex justify-content-between">
          <div id="loan-close-date">
            <div class="small text-muted-more">{{ $t('LoanCard.closeDate') }}</div>
            <div class="text-amount text-muted">{{ $d(loan.closeDate) }}</div>
          </div>
          <div id="loan-amount">
            <div class="small text-muted-more">{{ $t('LoanCard.loanAmount') }}</div>
            <div class="text-amount text-muted">{{ $n(loan.amount, 'currency') }}</div>
          </div>
          <div class="btn btn-link d-flex align-items-center mr-n3 button-loan-details">
            {{ $t('LoanDetailsPage.title') }}
          </div>
        </div>
      </div>
      <template v-else>
        <div class="card-body" v-if="loan.status === 'DEFAULTED'">
          <template v-if="isShownFullDetails">
            <div class="d-flex justify-content-between align-items-start">
              <div class="mr-3" id="loan-remaining-amount">
                <div class="small text-warning">{{ $t('LoanCard.finalDueAmount') }}</div>
                <div class="text-amount text-muted">{{ $n(loan.remainingAmount, 'currency') }}</div>
              </div>
              <div class="mr-3">
                <div class="small text-muted-more">{{ $t('LoanCard.reference') }}</div>
                <div class="text-amount text-muted">{{ loan.reference }}</div>
              </div>
              <router-link :to="{ name: 'loan', params: { id: loan.reference }}"
                           class="btn btn-link d-flex align-items-center mr-n3">
                <Icon icon-name="info-outline"></Icon>
              </router-link>
            </div>
            <router-link :to="{ name: 'external-payments', params: { id: loan.reference }}"
                         class="btn btn-primary btn-block mt-4">
              <span>{{ $t('LoanCard.payNow') }}</span>
            </router-link>
          </template>
          <template v-else>
            <div class="d-flex justify-content-between">
              <div>
                <div class="small text-muted-more">{{ $t('LoanCard.reference') }}</div>
                <div class="text-amount text-muted">{{ loan.reference }}</div>
              </div>
              <div class="btn btn-link d-flex align-items-center mr-n3 button-loan-details">
                {{ $t('LoanDetailsPage.title') }}
              </div>
            </div>
          </template>
        </div>
        <div id="loan-card-delayed" class="card-body" v-else-if="loan.status === 'DELAYED'">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div class="small text-warning">
                {{ $tc('LoanCard.overdueDays', loan.overdueDays) }}
              </div>
              <div class="text-amount">{{ $n(loan.toBePaid, 'currency') }}</div>
            </div>
            <div class="mr-3" id="loan-card-delayed-reference">
              <div class="small text-muted-more">{{ $t('LoanCard.reference') }}</div>
              <div class="text-amount text-muted">{{ loan.reference }}</div>
            </div>
          </div>
          <div class="progress mb-3">
            <div class="progress-bar bg-success" role="progressbar"
                 :style="{ width: loanBarWidth(loan.paidAmount, loan.remainingAmount) + '%' }"
                 aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
          </div>
          <div class="d-flex justify-content-between">
            <div class="mr-3" id="loan-card-delayed-remaining-amount">
              <div class="small text-muted-more">{{ $t('LoanCard.remaining') }}</div>
              <div class="text-amount text-muted">{{ $n(loan.remainingAmount, 'currency') }}</div>
            </div>
            <div class="btn btn-link d-flex align-items-center mr-n3 button-loan-details">
              {{ $t('LoanDetailsPage.title') }}
            </div>
          </div>
        </div>
        <div id="loan-card-unpaid" class="card-body" v-else-if="loan.status === 'NORMAL_UNPAID'">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div class="small text-warning">&nbsp;</div>
              <div class="text-amount">{{ $n(loan.toBePaid, 'currency') }}</div>
            </div>
            <div class="mr-3" id="loan-card-unpaid-reference">
              <div class="small text-muted-more">{{ $t('LoanCard.reference') }}</div>
              <div class="text-amount text-muted">{{ loan.reference }}</div>
            </div>
          </div>
          <div class="progress mb-3">
            <div class="progress-bar bg-success" role="progressbar"
                 :style="{ width: loanBarWidth(loan.paidAmount, loan.remainingAmount) + '%' }"
                 aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
          </div>
          <div class="d-flex justify-content-between">
            <div class="mr-3" id="loan-card-unpaid-remaining-amount">
              <div class="small text-muted-more">{{ $t('LoanCard.remaining') }}</div>
              <div class="text-amount text-muted">{{ $n(loan.remainingAmount, 'currency') }}</div>
            </div>
            <div class="btn btn-link d-flex align-items-center mr-n3 button-loan-details">
              {{ $t('LoanDetailsPage.title') }}
            </div>
          </div>
        </div>
        <div class="card-body" v-else>
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div class="small text-muted next-payment-in-days" v-if="loan.nextPaymentDate">
                {{ $tc('LoanCard.nextPaymentDays', loan.nextPaymentInDays) }}
              </div>
              <div class="text-amount next-payment-amount" v-if="loan.nextPaymentDate">{{
                  $n(loan.toBePaid, 'currency')
                }}
              </div>
            </div>
            <div>
              <div class="small text-muted-more">{{ $t('LoanCard.reference') }}</div>
              <div class="text-amount text-muted">{{ loan.reference }}</div>
            </div>
          </div>
          <div class="progress mb-3">
            <div class="progress-bar bg-success" role="progressbar"
                 :style="{ width: loanBarWidth(loan.paidAmount, loan.totalDebt) + '%' }" aria-valuenow="25"
                 aria-valuemin="0" aria-valuemax="100"></div>
          </div>
          <div class="d-flex justify-content-between align-items-start">
            <div class="mr-3">
              <div class="small text-muted-more">{{ $t('LoanCard.remaining') }}</div>
              <div class="text-amount text-muted">{{ $n(loan.remainingAmount, 'currency') }}</div>
            </div>
            <div class="btn btn-link d-flex align-items-center mr-n3 button-loan-details">
              {{ $t('LoanDetailsPage.title') }}
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Icon from '@shared/components/Icon'
import store from '@shared/store'
import LoanOverdueMessage from '@shared/components/loan/details/LoanOverdueMessage'
import LoanDefaultedMessage from '@shared/components/loan/details/LoanDefaultedMessage'
import LoanUnpaidMessage from '@shared/components/loan/details/LoanUnpaidMessage'

export default {
  name: 'LoanCard',
  data() {
    return {
      store: store.state
    }
  },
  components: { LoanDefaultedMessage, LoanOverdueMessage, LoanUnpaidMessage, Icon },
  props: ['loan', 'showFullDetailsForWrittenOff'],
  computed: {
    isShownFullDetails() {
      return !!this.showFullDetailsForWrittenOff || this.loan.subStatus !== 'WRITTEN_OFF'
    }
  },
  methods: {
    loanBarWidth: function (paid, totalDebt) {
      return (paid / totalDebt) * 100
    },
    openLoanDetails() {
      this.$router.push({ name: 'loan', params: { id: this.loan.reference } })
    }
  }
}
</script>
